import { useState } from "react";
import { Users, Link2, Copy, Check, Crown, Wifi, WifiOff, UserX, XCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import { API_URL } from "@/utils/apiConfig";
const API = API_URL;

export function CollaborationBar({ diagramId, connected, presence, myId, isEnterprise, onStartCollab, isOwner }) {
  const [collabLinkLoading, setCollabLinkLoading] = useState(false);
  const [collabLink, setCollabLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [closingCollab, setClosingCollab] = useState(false);

  const others = presence.filter(p => p.id !== myId);

  const kickUser = async (wsId, name) => {
    try {
      await axios.post(`${API}/diagrams/${diagramId}/collab-kick`, { ws_id: wsId }, { withCredentials: true });
      toast.success(`${name} a été déconnecté`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const closeCollab = async () => {
    setClosingCollab(true);
    try {
      await axios.delete(`${API}/diagrams/${diagramId}/collab`, { withCredentials: true });
      toast.success("Collaboration fermée");
      setCollabLink(null);
      setShowPanel(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
    setClosingCollab(false);
  };

  const generateCollabLink = async () => {
    if (!isEnterprise) return;
    setCollabLinkLoading(true);
    try {
      const { data } = await axios.post(`${API}/diagrams/${diagramId}/collab-link`, {}, { withCredentials: true });
      const link = `${window.location.origin}/collab/${data.collab_id}`;
      setCollabLink(link);
      toast.success("Lien de collaboration cree");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
    setCollabLinkLoading(false);
  };

  const copyLink = () => {
    if (collabLink) {
      navigator.clipboard.writeText(collabLink);
      setCopied(true);
      toast.success("Lien copié");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="relative" data-testid="collab-bar">
      {/* Collaboration button */}
      <button
        onClick={() => {
          if (!isEnterprise) {
            onStartCollab?.();
            return;
          }
          setShowPanel(v => !v);
        }}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold border transition-all ${
          connected
            ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
            : "bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100"
        }`}
        data-testid="collab-toggle-btn"
      >
        {connected ? <Wifi size={12} /> : <Users size={12} />}
        Collab
        {!isEnterprise && <Crown size={8} className="text-amber-400" />}
        {others.length > 0 && (
          <span className="bg-emerald-600 text-white text-[8px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
            {others.length}
          </span>
        )}
      </button>

      {/* Presence avatars */}
      {others.length > 0 && (
        <div className="flex items-center -space-x-1.5 ml-1">
          {others.slice(0, 4).map((p) => (
            <div
              key={p.id}
              className="w-6 h-6 rounded-full border-2 border-white flex items-center justify-center text-[8px] font-bold text-white shadow-sm"
              style={{ backgroundColor: p.color }}
              title={p.name}
            >
              {(p.name || "?")[0].toUpperCase()}
            </div>
          ))}
          {others.length > 4 && (
            <div className="w-6 h-6 rounded-full border-2 border-white bg-gray-400 flex items-center justify-center text-[8px] font-bold text-white shadow-sm">
              +{others.length - 4}
            </div>
          )}
        </div>
      )}

      {/* Panel dropdown */}
      {showPanel && isEnterprise && (
        <div className="absolute right-0 top-10 bg-white border border-gray-200 rounded-xl shadow-2xl w-72 z-[100] overflow-hidden" data-testid="collab-panel">
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-bold text-gray-900">Collaboration temps reel</h4>
              <div className="flex items-center gap-1">
                {connected ? (
                  <span className="text-[9px] text-emerald-600 font-semibold flex items-center gap-1"><Wifi size={9} /> Connecté</span>
                ) : (
                  <span className="text-[9px] text-gray-400 font-semibold flex items-center gap-1"><WifiOff size={9} /> Déconnecté</span>
                )}
              </div>
            </div>
          </div>

          {/* Connectéd users */}
          <div className="px-4 py-3 space-y-2">
            <p className="text-[9px] font-semibold text-gray-400 uppercase">Participants ({presence.length}/{5})</p>
            {presence.map((p) => (
              <div key={p.id} className="flex items-center gap-2">
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white shrink-0"
                  style={{ backgroundColor: p.color }}
                >
                  {(p.name || "?")[0].toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <span className="text-xs font-semibold text-gray-900 block truncate">
                    {p.name} {p.id === myId && <span className="text-gray-400">(vous)</span>}
                  </span>
                  <span className="text-[9px] text-gray-400 block truncate">{p.email}</span>
                </div>
                {p.id !== myId && isOwner ? (
                  <button
                    onClick={() => kickUser(p.id, p.name)}
                    className="w-6 h-6 rounded-md flex items-center justify-center text-gray-300 hover:text-rose-500 hover:bg-rose-50 transition-colors shrink-0"
                    title={`Déconnecter ${p.name}`}
                    data-testid={`kick-user-${p.id}`}
                  >
                    <UserX size={12} />
                  </button>
                ) : (
                  <div className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                )}
              </div>
            ))}
          </div>

          {/* Generate link */}
          <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50">
            {!collabLink ? (
              <button
                onClick={generateCollabLink}
                disabled={collabLinkLoading}
                className="w-full py-2 rounded-lg text-[10px] font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
                data-testid="generate-collab-link-btn"
              >
                <Link2 size={11} />
                {collabLinkLoading ? "Generation..." : "Générer un lien d'invitation"}
              </button>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-1.5">
                  <input
                    value={collabLink}
                    readOnly
                    className="flex-1 text-[9px] bg-white border border-gray-200 rounded-lg px-2 py-1.5 text-gray-600 truncate"
                    data-testid="collab-link-input"
                  />
                  <button
                    onClick={copyLink}
                    className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-100 transition-colors shrink-0"
                    data-testid="copy-collab-link-btn"
                  >
                    {copied ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} className="text-gray-400" />}
                  </button>
                </div>
                <p className="text-[8px] text-gray-400">Les invités doivent avoir un compte Entreprise pour collaborer.</p>
              </div>
            )}
            {/* Close collaboration */}
            {isOwner && connected && (
              <button
                onClick={closeCollab}
                disabled={closingCollab}
                className="w-full mt-2 py-2 rounded-lg text-[10px] font-bold bg-rose-50 text-rose-600 hover:bg-rose-100 border border-rose-200 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
                data-testid="close-collab-btn"
              >
                <XCircle size={11} />
                {closingCollab ? "Fermeture..." : "Fermer la collaboration"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
